/**
 * Mapper d'erreurs unifié pour tout le projet.
 * Convertit erreurs Supabase / réseau / validation en messages FR cohérents,
 * orientés utilisateur final (sans jargon technique).
 *
 * Usage :
 *   try { ... } catch (e) { toast.error(getErrorMessage(e)); }
 */

import { toast } from "sonner";

export type AppErrorContext =
  | "auth"
  | "session"
  | "form"
  | "fetch"
  | "mutation"
  | "upload"
  | "generic";

interface NormalizedError {
  message: string;
  code?: string;
  status?: number;
  hint?: string;
}

/**
 * Normalise une erreur arbitraire en structure exploitable.
 */
function normalize(err: unknown): NormalizedError {
  if (!err) return { message: "Erreur inconnue" };

  // Supabase PostgrestError / AuthError / FunctionsHttpError
  if (typeof err === "object" && err !== null) {
    const e = err as Record<string, unknown>;
    return {
      message: String(e.message ?? e.error_description ?? "Erreur inconnue"),
      code: e.code ? String(e.code) : undefined,
      status: typeof e.status === "number" ? e.status : undefined,
      hint: e.hint ? String(e.hint) : undefined,
    };
  }

  return { message: String(err) };
}

/**
 * Retourne un message FR utilisateur pour une erreur.
 * Tient compte du contexte pour adapter la formulation.
 */
export function getErrorMessage(err: unknown, context: AppErrorContext = "generic"): string {
  const n = normalize(err);
  const msg = n.message.toLowerCase();
  const status = n.status;
  const code = n.code?.toLowerCase();

  // ─── Réseau / hors-ligne ──────────────────────────────
  if (
    msg.includes("failed to fetch") ||
    msg.includes("network") ||
    msg.includes("networkerror") ||
    msg.includes("load failed")
  ) {
    return "Connexion internet instable. Vérifiez votre réseau et réessayez.";
  }

  // ─── Session / auth ───────────────────────────────────
  if (
    msg.includes("jwt expired") ||
    msg.includes("invalid jwt") ||
    msg.includes("invalid refresh token") ||
    msg.includes("refresh token not found") ||
    code === "pgrst301" ||
    code === "pgrst302"
  ) {
    return "Votre session a expiré. Veuillez vous reconnecter.";
  }

  if (msg.includes("invalid login credentials") || msg.includes("invalid_credentials")) {
    return "Email ou mot de passe incorrect.";
  }

  if (msg.includes("email not confirmed")) {
    return "Adresse e-mail non confirmée. Vérifiez votre boîte mail.";
  }

  if (msg.includes("user already registered") || msg.includes("already registered")) {
    return "Un compte existe déjà avec cet e-mail.";
  }

  if (msg.includes("password should be") || msg.includes("weak_password")) {
    return "Mot de passe trop faible (8 caractères minimum, avec majuscule et chiffre).";
  }

  if (msg.includes("rate limit") || msg.includes("too many requests") || status === 429) {
    return "Trop de tentatives. Patientez quelques minutes avant de réessayer.";
  }

  // ─── Validation / champs manquants ────────────────────
  if (
    code === "23502" || // not_null_violation
    msg.includes("null value in column") ||
    msg.includes("violates not-null")
  ) {
    return "Un champ obligatoire est manquant. Vérifiez le formulaire.";
  }

  if (
    code === "23505" || // unique_violation
    msg.includes("duplicate key") ||
    msg.includes("already exists")
  ) {
    return "Cette valeur existe déjà. Choisissez-en une autre.";
  }

  if (
    code === "23503" || // foreign_key_violation
    msg.includes("violates foreign key")
  ) {
    return "Référence invalide. L'élément lié n'existe pas ou plus.";
  }

  if (
    code === "23514" || // check_violation
    msg.includes("violates check constraint")
  ) {
    return "Valeur invalide. Vérifiez les contraintes du formulaire.";
  }

  // ─── HTTP statut ──────────────────────────────────────
  if (status === 400) return context === "form"
    ? "Formulaire invalide. Vérifiez les champs."
    : "Requête invalide.";
  if (status === 401) return "Session expirée. Veuillez vous reconnecter.";
  if (status === 403) return "Vous n'avez pas les droits pour cette action.";
  if (status === 404) return "Élément introuvable.";
  if (status === 409) return "Conflit : cette opération entre en conflit avec une autre.";
  if (status === 413) return "Fichier trop volumineux.";
  if (status === 422) return "Données invalides. Vérifiez le formulaire.";
  if (status && status >= 500 && status < 600) {
    return "Service temporairement indisponible. Réessayez dans quelques instants.";
  }

  // ─── Storage / upload ─────────────────────────────────
  if (context === "upload") {
    if (msg.includes("payload too large")) return "Fichier trop volumineux (max 5 Mo).";
    if (msg.includes("invalid mime")) return "Format de fichier non supporté.";
  }

  // ─── Fallback contextualisé ───────────────────────────
  switch (context) {
    case "auth":     return "Échec de l'authentification. Réessayez.";
    case "session":  return "Problème de session. Veuillez vous reconnecter.";
    case "form":     return "Erreur dans le formulaire. Vérifiez les champs.";
    case "fetch":    return "Impossible de charger les données. Réessayez.";
    case "mutation": return "L'enregistrement a échoué. Réessayez.";
    case "upload":   return "L'envoi du fichier a échoué.";
    default:         return n.message || "Une erreur est survenue.";
  }
}

/**
 * Helper one-liner : affiche un toast d'erreur uniformisé.
 */
export function toastError(err: unknown, context: AppErrorContext = "generic"): void {
  toast.error(getErrorMessage(err, context));
}

/**
 * Helper one-liner : affiche un toast de succès uniformisé.
 */
export function toastSuccess(message: string, description?: string): void {
  toast.success(message, description ? { description } : undefined);
}

/**
 * Wrapper pour exécuter une mutation/action async avec gestion d'erreur
 * uniformisée. Affiche un toast d'erreur via `toastError` si la promesse
 * échoue, et un toast de succès si `successMessage` est fourni.
 *
 * Renvoie `{ data, error }` au lieu de throw, pour ne pas casser le flux UI.
 *
 * Exemple :
 *   const { error } = await runMutation(
 *     () => supabase.from("articles").update(...).eq("id", id),
 *     { context: "mutation", successMessage: "Article mis à jour" }
 *   );
 *   if (!error) refetch();
 */
export async function runMutation<T>(
  fn: () => Promise<T>,
  opts: {
    context?: AppErrorContext;
    successMessage?: string;
    successDescription?: string;
    /** Si true, ne toaste pas l'erreur (utile si on veut juste capturer). */
    silent?: boolean;
  } = {},
): Promise<{ data: T | null; error: unknown }> {
  const { context = "mutation", successMessage, successDescription, silent } = opts;
  try {
    const data = await fn();
    // Détection d'erreur Supabase : `{ data, error }` retourné par le client.
    if (data && typeof data === "object" && "error" in data && (data as { error: unknown }).error) {
      const err = (data as { error: unknown }).error;
      if (!silent) toastError(err, context);
      return { data: null, error: err };
    }
    if (successMessage) toastSuccess(successMessage, successDescription);
    return { data, error: null };
  } catch (err) {
    if (!silent) toastError(err, context);
    return { data: null, error: err };
  }
}

/**
 * Détecte si une erreur indique une session expirée (pour redirect global).
 */
export function isSessionExpired(err: unknown): boolean {
  const n = normalize(err);
  const msg = n.message.toLowerCase();
  return (
    n.status === 401 ||
    msg.includes("jwt expired") ||
    msg.includes("invalid jwt") ||
    msg.includes("invalid refresh token") ||
    msg.includes("refresh token not found")
  );
}
